import type { ColumnDef } from "@tanstack/react-table";
import type { AxiosError } from "axios";
import type { MouseEvent } from "react";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { StringParam, useQueryParams } from "use-query-params";
import { useAuthContext } from "../../../../components/Auth";
import { PrimaryButtonWithPlusIcon } from "../../../../components/Buttons/Buttons";
import { CreateNewUser } from "../../../../components/CreateNewUser/CreateNewUser";
import { EditUser } from "../../../../components/EditUser/EditUser";
import { Modal } from "../../../../components/Modal/Modal";
import { Notifications } from "../../../../components/Notifications/NotificationsContext";
import { SlideOut } from "../../../../components/SlideOut/SlideOut";
import { StatusRight } from "../../../../components/Status/Status";
import { TablePageLayout } from "../../../../components/TablePage/TablePageLayout";
import { endpoints } from "../../../../endpoints";
import { ErrorHandler } from "../../../../ErrorHandler";
import type {
  IPagination,
  User,
  UserType,
  UserTypeChip,
} from "../../../../types/types";
import { useDebounce } from "../../../../util/hooks";
import { getCountryOption } from "../../../../util/location";
import { useCountriesList } from "../../../../util/Locations";
import { createChangePerPageHandler } from "../../../../components/Pagination/util";
import {
  DEFAULT_PER_PAGE,
  makeUrlWithParams,
  useStoreState,
} from "../../../../util/util";
import { getUserTypeForUi } from "../../../../util/util-components";

type UsersAPIResponse = {
  data: User[];
  pagination: IPagination;
};

type UsersTable = {
  id: string;
  name: string;
  account_type: string;
  //created_date: string;
  is_active: boolean;
};

/**
 * The "Manage > Users" page.  Used for:
 *   - Seller Admin
 *   - Buyer Admin
 *
 * Use the tenant ID from the user object to show and create users for
 * the correct tenant.  The Seller Admin's tenant is the same as the
 * storefront tenant.
 */
export function ManageUsersList({ user }: { user: User }) {
  const [query, setQuery] = useQueryParams({
    q: StringParam,
  });
  const { storefront_id, storefront_metadata } = useStoreState();
  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState(query.q || "");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const [selectedUser, setSelectedUser] = useState<User>();
  const [offset, setOffset] = useState(0);
  const perPageItems = [10, 20, 50];
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const {
    roleIsSellerAdmin,
    roleIsBuyerAdmin,
    roleIsSomeKindOfSeller,
    roleIsDistributorAdmin,
  } = useAuthContext();

  const countries = useCountriesList();

  const [tablePagination, setTablePagination] = useState({
    perPage: perPage,
    pageCount: 0,
    pageIndex: 0,
  });

  const [showModal, setShowModal] = useState(false);
  const [showEditSlideOut, setEditSlideOut] = useState(false);

  const { notifyError } = useContext(Notifications);

  const {
    data: usersResponse,
    error: usersError,
    mutate,
  } = useSWR<UsersAPIResponse, AxiosError>(
    makeUrlWithParams(
      endpoints.v1_storefronts_id_tenants_id_users(
        storefront_id,
        user.tenant_id
      ),
      {
        offset: offset,
        limit: perPage,
        q: debouncedSearchQuery || null,
        view: "full",
      }
    ),
    { revalidateOnMount: true }
  );

  const isLoading = !usersResponse && !usersError;

  const [tableData, setTableData] = useState<UsersTable[]>([]);
  const tableColumns = React.useMemo<ColumnDef<UsersTable>[]>(
    () => [
      {
        header: t("Name"),
        accessorKey: "name",
      },
      {
        header: roleIsSomeKindOfSeller ? t("Role") : t("Account Type"),
        accessorKey: "account_type",
      },
      ...(roleIsSomeKindOfSeller
        ? [
            ...(usersResponse?.data?.some((user) => !!user.business_unit)
              ? [
                  {
                    header: t("Business Unit"),
                    accessorKey: "business_unit",
                  },
                ]
              : []),
            ...(usersResponse?.data?.some((user) => !!user.country)
              ? [
                  {
                    header: t("Country"),
                    accessorKey: "country",
                  },
                ]
              : []),
          ]
        : []),
      {
        header: t("Status"),
        accessorKey: "is_active",
        width: 50,
        minWidth: 50,
        align: "right",
        cell: (cell) => (
          <StatusRight
            color={cell.getValue() ? "green" : "red"}
            text={cell.getValue() ? t("Active") : t("Inactive")}
          />
        ),
      },
    ],
    [t, roleIsSomeKindOfSeller, usersResponse?.data]
  );

  useEffect(() => {
    const handleUsersData = ({ data, pagination }: UsersAPIResponse) => {
      setTableData(
        data
          .filter((user) => !user.is_primary_contact)
          .map((user) => ({
            id: user.id,
            name: `${user.firstname || ""} ${user.lastname || ""}`,
            account_type: user.role
              ? user.role.name
              : getUserTypeForUi(user.user_type),
            business_unit: user.business_unit,
            country: getCountryOption(countries, user.country)?.label || "",
            is_active: user.is_active,
          }))
      );
      setTablePagination({
        perPage: perPage,
        pageCount: Math.ceil(pagination.total / perPage),
        pageIndex: pagination.offset / perPage + 1,
      });
    };

    if (usersResponse) {
      const { data: users, pagination } = usersResponse;
      handleUsersData({ data: users, pagination });
    }
  }, [usersResponse, setTableData, perPage, countries]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOffset(0);
    setSearchQuery(e.target.value);
  };
  const handleClearSearch = () => {
    setSearchQuery("");
    setQuery({ q: undefined });
  };

  const changePerPage = createChangePerPageHandler(
    offset,
    setPerPage,
    setOffset
  );

  const changePage = (offset: number) => {
    setOffset(offset);
    setTableData([]);
  };

  useEffect(() => {
    // This useEffect handles setting the selected product,
    // and keeping the selected product and query in sync with the URL.
    if (debouncedSearchQuery === "") setQuery({ q: undefined });
    if (debouncedSearchQuery) {
      // update the URL every time the debounced query changes
      setQuery({ q: debouncedSearchQuery });
    }
  }, [setQuery, query, debouncedSearchQuery]);

  const handleRowClick = (e: MouseEvent) => {
    if (usersResponse) {
      setSelectedUser(
        usersResponse.data.find((user: User) => user.id === e.currentTarget.id)
      );
      setEditSlideOut(true);
    } else {
      notifyError(t("There was an error fetching the user"));
    }
  };

  const openCreateUserModal = () => setShowModal(true);
  const closeCreateUserModal = () => setShowModal(false);

  const closeEditSlideOut = () => {
    setEditSlideOut(false);
  };

  const userChipDetails = ((): {
    chips: UserTypeChip[];
    default: UserType;
  } | null => {
    if (roleIsBuyerAdmin) {
      return {
        chips: [{ name: "Admin", id: "Buyer Admin" }],
        default: "Buyer Admin",
      };
    }
    if (roleIsDistributorAdmin) {
      return {
        chips: [{ name: "Distributor", id: "Distributor Admin" }],
        default: "Distributor Admin",
      };
    }
    if (roleIsSellerAdmin) {
      // @Paul when you review this why is the below line here?
      // if (isStarterEdition) {
      return {
        chips: [
          { name: "Admin", id: "Seller Admin" },
          { name: "Standard", id: "Seller Standard" },
        ],
        default: "Seller Admin",
      };
    }
    // Should never happen.
    console.error("Unsupported user type:", user.rbac_role);
    ErrorHandler.report(`Unsupported user type: ${user.rbac_role}`);
    return null;
  })();

  return (
    <>
      <TablePageLayout
        searchQuery={searchQuery}
        searchPlaceholder={t("Search for users by name")}
        onSearchChange={handleSearch}
        onSearchClear={handleClearSearch}
        toolbarActions={
          userChipDetails && !storefront_metadata.sso_only ? (
            <PrimaryButtonWithPlusIcon onClick={openCreateUserModal} useV2>
              {t("Create New")}
            </PrimaryButtonWithPlusIcon>
          ) : undefined
        }
        perPageItems={perPageItems}
        perPage={perPage}
        onPerPageChange={changePerPage}
        columns={tableColumns}
        data={tableData}
        isLoading={isLoading}
        error={usersError}
        rowClick={handleRowClick}
        pagination={tablePagination}
        totalCount={usersResponse?.pagination?.total}
        offset={offset}
        onPageChange={changePage}
      />
      {userChipDetails && !storefront_metadata.sso_only && (
        <Modal closeModal={closeCreateUserModal} show={showModal}>
          <CreateNewUser
            tenantId={user.tenant_id}
            userTypeChips={userChipDetails.chips}
            defaultUserType={userChipDetails.default}
            userFormType="tenant"
            tenantTypeToFetch="Buyer"
            onSuccess={() => {
              closeCreateUserModal();
              mutate();
            }}
            onCancel={closeCreateUserModal}
          />
        </Modal>
      )}
      <SlideOut
        closeFlyout={closeEditSlideOut}
        show={showEditSlideOut}
        title={t("Edit user")}
      >
        {selectedUser && userChipDetails && (
          <EditUser
            user={selectedUser}
            userTypeChips={userChipDetails.chips}
            onSuccess={() => {
              closeEditSlideOut();
              // force SWR to revalidate
              mutate();
            }}
            onCancel={closeEditSlideOut}
            countries={countries}
          />
        )}
      </SlideOut>
    </>
  );
}
